import axios from 'axios';
import { ApiResponse, ImageAnalysisResult, MultiImageAnalysisResult, User, ChatMessage, TreatmentPlanResponse } from '../types';
import logger from '../utils/logger';
import authService from './authApi';

// Configuration - matches config.py
const API_BASE_URL = process.env.REACT_APP_ORTHOMIND_API_URL || 'https://api.orthomind.ai'; // Read from environment or default to production
logger.log('🚀 API Base URL being used:', API_BASE_URL);
logger.log('🔍 Environment variable:', process.env.REACT_APP_ORTHOMIND_API_URL);

// User ID will be set from authentication
let CURRENT_USER_ID: string | null = null;

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 2 minutes for LLM processing
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create a separate instance for deep research with longer timeout
const deepResearchApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes for deep research
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for logging
api.interceptors.request.use((config) => {
  logger.log(`🔄 API Request: ${config.method?.toUpperCase()} ${config.url}`, config.data);
  return config;
});

// Add response interceptor for logging
api.interceptors.response.use(
  (response) => {
    logger.log(`✅ API Response: ${response.status}`, response.data);
    return response;
  },
  (error) => {
    logger.error(`❌ API Error:`, error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Add interceptors for deep research API
deepResearchApi.interceptors.request.use((config) => {
  logger.log(`🔬 Deep Research Request: ${config.method?.toUpperCase()} ${config.url}`, config.data);
  return config;
});

deepResearchApi.interceptors.response.use(
  (response) => {
    logger.log(`✅ Deep Research Response: ${response.status}`, response.data);
    return response;
  },
  (error) => {
    logger.error(`❌ Deep Research Error:`, error.response?.data || error.message);
    return Promise.reject(error);
  }
);

export class ApiService {
  private sessionId: string | null = null;
  private conversationContext: string = 'general'; // Default to general (deep research)
  private lastBotQuestion: string = '';
  private authToken: string | null = null;
  private userId: string | null = null;

  // Set authentication token
  setAuthToken(token: string) {
    this.authToken = token;
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    deepResearchApi.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  // Set user ID
  setUserId(userId: string) {
    this.userId = userId;
    CURRENT_USER_ID = userId;
  }

  // Clear authentication
  clearAuth() {
    this.authToken = null;
    this.userId = null;
    CURRENT_USER_ID = null;
    delete api.defaults.headers.common['Authorization'];
    delete deepResearchApi.defaults.headers.common['Authorization'];
  }

  // Get current authenticated user
  async getCurrentAuthUser(token: string): Promise<User | null> {
    try {
      const userData = await authService.getCurrentUser(token);
      return {
        id: userData.user_id,
        name: userData.name,
        email: userData.email,
        role: userData.role,
      };
    } catch (error) {
      logger.error('Failed to get current user:', error);
      return null;
    }
  }

  // Authentication methods
  async signIn(email: string, password: string) {
    return authService.signIn(email, password);
  }

  async signUp(email: string, password: string, name: string) {
    return authService.signUp(email, password, name);
  }

  async signOut(token: string) {
    return authService.signOut(token);
  }

  async resetPassword(email: string) {
    return authService.resetPassword(email);
  }

  async refreshToken(refresh_token: string) {
    return authService.refreshToken(refresh_token);
  }

  // Session Management
  async createSession(): Promise<string> {
    // Clear any existing session first
    this.sessionId = null;
    
    // Validate we have a real user ID (not test-user)
    const userIdToUse = this.userId || CURRENT_USER_ID;
    if (!userIdToUse || userIdToUse === 'test-user') {
      throw new Error('Cannot create session: User not authenticated. Please sign in first.');
    }
    
    try {
      const response = await api.post('/sessions/create', {
        source_type: 'manual',
        user_id: userIdToUse,
      });

      if (!response.data.session_id) {
        throw new Error('Backend did not return a session_id');
      }

      this.sessionId = response.data.session_id;
      logger.log(`Created new session from backend: ${this.sessionId} for user: ${userIdToUse}`);
      return this.sessionId as string; // We know it's not null because we checked above
    } catch (error: any) {
      // DO NOT create local session IDs - this causes sync issues
      logger.error('Failed to create session:', error);
      throw new Error(`Cannot create session: ${error.message || 'Backend unavailable'}`);
    }
  }

  async getOrCreateSession(): Promise<string> {
    if (!this.sessionId) {
      // This will throw if backend is unavailable - that's intentional
      // We should never proceed without a valid backend session
      await this.createSession();
    }
    
    if (!this.sessionId) {
      throw new Error('No valid session ID available');
    }
    
    return this.sessionId;
  }

  // Chat API - for all messages
  async sendMessage(message: string): Promise<ApiResponse> {
    const sessionId = await this.getOrCreateSession();

    try {
      // Use chat endpoint for all messages
      const context = {
        message,
        session_id: sessionId,
        last_question: this.lastBotQuestion,
        conversation_context: this.conversationContext,
      };

      const response = await api.post('/chat', context);

      const result = response.data;
      logger.log(`✅ Chat success: ${result.action}`);
      logger.log(`📋 Response keys: ${Object.keys(result)}`);
      
      // Log treatment response if present
      if (result.treatment_response) {
        logger.log(`🏥 Treatment response present: ${result.treatment_response.substring(0, 200)}...`);
      }
      
      // Strip whitespace from response for treatment planning
      if (result.response && typeof result.response === 'string') {
        result.response = result.response.trim();
      }
      
      // Also trim treatment_response if present
      if (result.treatment_response && typeof result.treatment_response === 'string') {
        result.treatment_response = result.treatment_response.trim();
      }
      
      logger.log(`📝 Response preview: ${result.response?.substring(0, 200)}...`);

      // Extract bot question from response for context
      this.lastBotQuestion = this.extractBotQuestion(result.response) || '';

      return result;
    } catch (error: any) {
      logger.error(`❌ API call failed:`, error);
      return {
        response: "I'm having trouble processing your request. Please try again.",
        action: 'error',
        metadata: { error: error.message },
      };
    }
  }

  // Deep Research API - for orthodontic questions
  async performDeepResearch(
    query: string, 
    researchType: 'comprehensive' | 'focused' | 'literature_review' = 'comprehensive',
    onProgress?: (status: string, progress?: number) => void
  ): Promise<ApiResponse> {
    const sessionId = await this.getOrCreateSession();

    try {
      const payload = {
        query,
        research_type: researchType,
        max_depth: researchType === 'focused' ? 2 : 3, // Use depth 2 for focused (optimal performance)
        include_sources: researchType !== 'focused', // Don't include sources for focused (faster)
        session_id: sessionId || null
      };

      logger.log('🔬 Performing deep research with streaming:', payload);
      
      // Use streaming endpoint
      const response = await fetch(`${API_BASE_URL}/deep-research-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let finalResult: any = null;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.trim() && line.startsWith('data: ')) {
            const data = line.slice(6); // Remove 'data: ' prefix
            
            if (data === '[DONE]') {
              logger.log('✅ Research stream completed');
              break;
            }
            
            try {
              const parsed = JSON.parse(data);
              
              // Update progress
              if (parsed.status && onProgress) {
                onProgress(parsed.status, parsed.progress);
              }
              
              // Store the final result
              if (parsed.final_result) {
                finalResult = parsed.final_result;
                logger.log('📝 Received final result:', finalResult);
              }
              
              // Also check for direct result format
              if (parsed.research_result) {
                finalResult = parsed;
                logger.log('📝 Received direct result format:', finalResult);
              }
              
              logger.log('📊 Stream update:', parsed.status);
            } catch (e) {
              logger.warn('Failed to parse stream data:', e);
            }
          }
        }
      }

      // Log the final result for debugging
      logger.log('📋 Final streaming result:', finalResult);
      
      // Process the final result - check multiple possible formats
      let research = null;
      
      if (finalResult) {
        // Format 1: { status: 'success', research_result: {...} }
        if (finalResult.status === 'success' && finalResult.research_result) {
          research = finalResult.research_result;
        }
        // Format 2: Direct research result
        else if (finalResult.summary || finalResult.key_findings) {
          research = finalResult;
        }
        // Format 3: Nested in data
        else if (finalResult.data && (finalResult.data.research_result || finalResult.data.summary)) {
          research = finalResult.data.research_result || finalResult.data;
        }
      }
      
      if (research) {
        
        // Format the response with markdown
        let formattedResponse = `## Research Summary\n\n${research.summary}\n\n`;
        
        if (research.key_findings && research.key_findings.length > 0) {
          formattedResponse += `### Key Findings\n\n`;
          research.key_findings.forEach((finding: string) => {
            formattedResponse += `- ${finding}\n\n`;
          });
        }

        if (research.recommendations && research.recommendations.length > 0) {
          formattedResponse += `### Evidence-Based Recommendations\n\n`;
          research.recommendations.forEach((rec: string) => {
            formattedResponse += `- ${rec}\n\n`;
          });
        }

        if (research.clinical_guidelines && research.clinical_guidelines.length > 0) {
          formattedResponse += `### Clinical Guidelines\n\n`;
          research.clinical_guidelines.forEach((guideline: string) => {
            formattedResponse += `- ${guideline}\n\n`;
          });
        }

        if (research.areas_for_further_research && research.areas_for_further_research.length > 0) {
          formattedResponse += `### Areas for Further Research\n\n`;
          research.areas_for_further_research.forEach((area: string) => {
            formattedResponse += `- ${area}\n\n`;
          });
        }

        if (research.sources && research.sources.length > 0) {
          formattedResponse += `### References\n\n`;
          research.sources.slice(0, 10).forEach((source: any, index: number) => {
            formattedResponse += `${index + 1}. ${source.full_citation}\n\n`;
          });
        }

        return {
          response: formattedResponse,
          action: 'research',
          metadata: {
            research_id: research.research_id,
            sources_count: research.sources?.length || 0,
            research_type: research.research_type,
            from_cache: research.metadata?.from_cache || false,
            aspects_researched: research.metadata?.aspects_researched || []
          }
        };
      }

      // If we didn't get proper streaming data, fall back to classic API
      logger.warn('Streaming response incomplete, falling back to classic API');
      return this.performDeepResearchClassic(query, researchType);
    } catch (error: any) {
      logger.error(`❌ Deep research failed:`, error);
      
      // Handle timeout specifically
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        return {
          response: "Research synthesis timed out. Please try again with a simpler query.",
          action: 'error',
          metadata: { error: 'timeout', originalError: error.message },
        };
      }
      
      // Handle other errors
      return {
        response: error.response?.data?.detail || "I'm having trouble performing the research. Please try again.",
        action: 'error',
        metadata: { error: error.message },
      };
    }
  }

  // Deep Research API (non-streaming) - fallback option
  async performDeepResearchClassic(
    query: string, 
    researchType: 'comprehensive' | 'focused' | 'literature_review' = 'focused'
  ): Promise<ApiResponse> {
    const sessionId = await this.getOrCreateSession();

    try {
      const payload = {
        query,
        research_type: researchType,
        max_depth: researchType === 'focused' ? 2 : 3,
        include_sources: researchType !== 'focused',
        session_id: sessionId || null
      };

      logger.log('🔬 Performing deep research (classic):', payload);
      const response = await deepResearchApi.post('/deep-research', payload);

      const result = response.data;
      logger.log(`✅ Deep research success`);
      logger.log(`📋 Research result:`, result);

      // Format the response to match our ApiResponse interface
      if (result.status === 'success' && result.research_result) {
        const research = result.research_result;
        
        // Format the response with markdown
        let formattedResponse = `## Research Summary\n\n${research.summary}\n\n`;
        
        if (research.key_findings && research.key_findings.length > 0) {
          formattedResponse += `### Key Findings\n\n`;
          research.key_findings.forEach((finding: string) => {
            formattedResponse += `- ${finding}\n\n`;
          });
        }

        if (research.recommendations && research.recommendations.length > 0) {
          formattedResponse += `### Evidence-Based Recommendations\n\n`;
          research.recommendations.forEach((rec: string) => {
            formattedResponse += `- ${rec}\n\n`;
          });
        }

        if (research.clinical_guidelines && research.clinical_guidelines.length > 0) {
          formattedResponse += `### Clinical Guidelines\n\n`;
          research.clinical_guidelines.forEach((guideline: string) => {
            formattedResponse += `- ${guideline}\n\n`;
          });
        }

        if (research.areas_for_further_research && research.areas_for_further_research.length > 0) {
          formattedResponse += `### Areas for Further Research\n\n`;
          research.areas_for_further_research.forEach((area: string) => {
            formattedResponse += `- ${area}\n\n`;
          });
        }

        if (research.sources && research.sources.length > 0) {
          formattedResponse += `### References\n\n`;
          research.sources.slice(0, 10).forEach((source: any, index: number) => {
            formattedResponse += `${index + 1}. ${source.full_citation}\n\n`;
          });
        }

        return {
          response: formattedResponse,
          action: 'research',
          metadata: {
            research_id: research.research_id,
            sources_count: research.sources?.length || 0,
            research_type: research.research_type,
            from_cache: research.metadata?.from_cache || false,
            aspects_researched: research.metadata?.aspects_researched || []
          }
        };
      }

      return {
        response: result.message || "Research completed successfully.",
        action: 'research',
        metadata: result
      };
    } catch (error: any) {
      logger.error(`❌ Deep research failed:`, error);
      
      // Handle timeout specifically
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        return {
          response: "Research synthesis timed out. Please try again with a simpler query or use 'Focused' research type.",
          action: 'error',
          metadata: { error: 'timeout', originalError: error.message },
        };
      }
      
      // Handle other errors
      return {
        response: error.response?.data?.detail || "I'm having trouble performing the research. Please try again.",
        action: 'error',
        metadata: { error: error.message },
      };
    }
  }

  // Ask Questions API - for quick orthodontic Q&A
  async askQuestion(question: string, language: string = 'en'): Promise<ApiResponse> {
    const sessionId = this.getCurrentSessionId();

    try {
      const payload = {
        question,
        language,
        session_id: sessionId || null
      };

      logger.log('❓ Asking question:', payload);
      const response = await api.post('/ask-questions', payload);

      const result = response.data;
      logger.log(`✅ Ask Questions success`);
      logger.log(`📋 Answer received with ${result.references?.length || 0} references`);

      // Format the response to match our ApiResponse interface
      let formattedResponse = result.answer || '';
      
      // Add references section if available
      if (result.references && result.references.length > 0) {
        formattedResponse += '\n\n### References\n\n';
        result.references.forEach((ref: string, index: number) => {
          formattedResponse += `${index + 1}. ${ref}\n`;
        });
      }

      return {
        response: formattedResponse,
        action: 'ask_question',
        metadata: {
          question: result.question,
          references_count: result.references?.length || 0,
          language: result.language,
          session_id: result.session_id
        }
      };
    } catch (error: any) {
      logger.error(`❌ Ask Questions failed:`, error);
      
      return {
        response: error.response?.data?.detail || "I'm having trouble answering your question. Please try again.",
        action: 'error',
        metadata: { error: error.message },
      };
    }
  }

  // Image Analysis API - supports single or multiple images
  async analyzeImage(imageBase64: string): Promise<ImageAnalysisResult>;
  async analyzeImage(imageBase64: string[]): Promise<MultiImageAnalysisResult>;
  async analyzeImage(imageBase64: string | string[]): Promise<ImageAnalysisResult | MultiImageAnalysisResult> {
    const sessionId = await this.getOrCreateSession();
    const isDev = process.env.NODE_ENV === 'development';
    const isMultiple = Array.isArray(imageBase64);

    try {
      // Handle both single and multiple images
      const endpoint = isMultiple 
        ? `/sessions/${sessionId}/analyze-images-base64` 
        : `/sessions/${sessionId}/analyze-image-base64`;
      
      const payload = isMultiple 
        ? { images_base64: imageBase64 }
        : { image_base64: imageBase64 };

      if (isDev) {
        logger.log('🔄 [API] Image analysis request:', {
          endpoint,
          sessionId,
          isMultiple,
          imageCount: isMultiple ? imageBase64.length : 1,
          payloadSize: JSON.stringify(payload).length
        });
      }

      const response = await api.post(endpoint, payload);

      if (isDev) {
        logger.log('✅ [API] Image analysis response:', {
          status: response.status,
          hasData: !!response.data,
          dataKeys: Object.keys(response.data || {}),
          fullResponse: response.data
        });
      }
      
      return response.data;
    } catch (error: any) {
      if (isDev) {
        logger.error('❌ [API] Image analysis failed:', {
          error,
          message: error.message,
          response: error.response?.data,
          status: error.response?.status,
          endpoint: isMultiple 
            ? `/sessions/${sessionId}/analyze-images-base64` 
            : `/sessions/${sessionId}/analyze-image-base64`
        });
      } else {
        logger.error('❌ Image analysis failed:', error.message);
      }
      throw new Error(error.response?.data?.detail || 'Failed to analyze image(s)');
    }
  }

  // Get current user from auth or mock for testing
  async getCurrentUser(): Promise<User> {
    // Try to get authenticated user first
    const token = this.authToken || localStorage.getItem('access_token');
    if (token) {
      const authUser = await this.getCurrentAuthUser(token);
      if (authUser) {
        return authUser;
      }
    }
    
    // Fallback to mock user for testing
    return {
      id: this.userId || 'test-user',
      email: 'test@orthomind.com',
      name: 'Test User',
      role: 'orthodontist',
    };
  }

  // Utility Methods
  private extractBotQuestion(responseText: string): string | null {
    if (!responseText) return null;

    const lines = responseText.split('\n');
    for (let i = lines.length - 1; i >= 0; i--) {
      const line = lines[i].trim();
      if (line && line.includes('?')) {
        return line;
      }
    }
    return null;
  }

  // Health Check
  async checkHealth(): Promise<boolean> {
    try {
      const response = await api.get('/health');
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }

  // File Upload Helper
  async uploadFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        // Remove data:image/...;base64, prefix
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  // Set conversation context
  setConversationContext(context: string) {
    this.conversationContext = context;
  }

  // Get current conversation context
  getCurrentContext(): string {
    return this.conversationContext;
  }

  // Get current session ID
  getCurrentSessionId(): string | null {
    return this.sessionId;
  }

  // Reset session (for new chat)
  resetSession() {
    this.sessionId = null;
    this.conversationContext = 'general'; // Default to general (deep research)
    this.lastBotQuestion = '';
  }
  
  // Set session ID (for switching between sessions)
  setSessionId(sessionId: string) {
    this.sessionId = sessionId;
  }

  // Get session details
  async getSessionDetails(sessionId?: string): Promise<any> {
    const id = sessionId || this.sessionId;
    if (!id) {
      throw new Error('No session ID available');
    }

    try {
      const response = await api.get(`/sessions/${id}`);
      logger.log(`✅ Session details fetched:`, response.data);
      return response.data;
    } catch (error: any) {
      logger.error(`❌ Failed to fetch session details:`, error);
      throw error;
    }
  }

  // Note: Chat messages are automatically saved by the backend through the /chat endpoint
  // No need for manual message saving

  // Get chat messages from backend
  async getChatMessages(sessionId: string, limit: number = 50): Promise<ChatMessage[]> {
    try {
      const response = await api.get(`/sessions/${sessionId}/chat-messages`, {
        params: { limit }
      });
      
      // Filter messages to only include those from July 29, 2025 onwards
      const START_DATE = new Date('2025-07-29');
      START_DATE.setHours(0, 0, 0, 0);
      
      return response.data.messages
        .map((msg: any) => ({
          id: msg.id,
          content: msg.content,
          role: msg.message_type === 'user_input' ? 'user' : 'assistant',
          timestamp: new Date(msg.timestamp),
          metadata: msg.metadata
        }))
        .filter((msg: ChatMessage) => msg.timestamp >= START_DATE);
    } catch (error) {
      logger.error('Failed to get messages:', error);
      return [];
    }
  }

  // Delete session from backend
  async deleteSession(sessionId: string): Promise<boolean> {
    try {
      await api.delete(`/sessions/${sessionId}`);
      return true;
    } catch (error) {
      logger.error('Failed to delete session:', error);
      return false;
    }
  }

  // Get Treatment Plan (previously generateTreatmentPlan)
  // Now retrieves the treatment plan that was generated via chat
  async getTreatmentPlan(sessionId: string): Promise<TreatmentPlanResponse> {
    try {
      logger.log('📋 Getting treatment plan for session:', sessionId);
      const response = await api.get(`/sessions/${sessionId}/plan`);
      
      const result = response.data;
      logger.log('✅ Treatment plan retrieved successfully');
      logger.log('📊 Treatment plan data:', result);
      
      return result;
    } catch (error: any) {
      logger.error('❌ Failed to get treatment plan:', error);
      throw error;
    }
  }

  // Get Case Criteria for a session
  async getCaseCriteria(sessionId: string): Promise<any> {
    try {
      logger.log('📋 Getting case criteria for session:', sessionId);
      const response = await api.get(`/sessions/${sessionId}/case-criteria`);
      
      const result = response.data;
      logger.log('✅ Case criteria retrieved successfully');
      logger.log('📊 Case criteria data:', result);
      
      return result;
    } catch (error: any) {
      logger.error('❌ Failed to get case criteria:', error);
      throw error;
    }
  }

  // Trigger treatment plan generation via chat
  async triggerTreatmentPlanGeneration(sessionId: string): Promise<ApiResponse> {
    try {
      logger.log('🤖 Triggering treatment plan generation via chat for session:', sessionId);
      
      // Send a chat message to trigger the generation
      const response = await this.sendMessage('generate treatment plan');
      
      logger.log('✅ Treatment plan generation triggered');
      return response;
    } catch (error: any) {
      logger.error('❌ Failed to trigger treatment plan generation:', error);
      throw error;
    }
  }

  // Keep the old method name for backward compatibility but redirect to new one
  async generateTreatmentPlan(sessionId: string): Promise<TreatmentPlanResponse> {
    return this.getTreatmentPlan(sessionId);
  }

  // Store Scan Record
  async storeScanRecord(scanData: {
    fullName: string;
    email: string;
    timestamp: string;
    fileNames: string[];
    sessionId?: string;
    analysisResults?: any;
  }): Promise<any> {
    try {
      logger.log('💾 Storing scan record:', scanData);
      
      // Option 1: Store with session
      if (scanData.sessionId) {
        const response = await api.post(`/sessions/${scanData.sessionId}/scan-record`, {
          full_name: scanData.fullName,
          email: scanData.email,
          file_names: scanData.fileNames,
          analysis_results: scanData.analysisResults
        });
        return response.data;
      }
      
      // Option 2: Store independently
      const response = await api.post('/scan-records', {
        full_name: scanData.fullName,
        email: scanData.email,
        timestamp: scanData.timestamp,
        file_names: scanData.fileNames,
        analysis_results: scanData.analysisResults
      });
      
      logger.log('✅ Scan record stored successfully');
      return response.data;
    } catch (error: any) {
      logger.error('❌ Failed to store scan record:', error);
      // Fall back to localStorage if API fails
      logger.warn('Falling back to localStorage');
      const existingScans = JSON.parse(localStorage.getItem('orthoMindScans') || '[]');
      existingScans.push(scanData);
      localStorage.setItem('orthoMindScans', JSON.stringify(existingScans));
      return { stored_locally: true };
    }
  }

  // Upload PDF to server
  async uploadPDF(pdfBlob: Blob, filename: string, scanRecordId?: string): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('pdf', pdfBlob, filename);
      if (scanRecordId) {
        formData.append('scan_record_id', scanRecordId);
      }
      
      logger.log('📄 Uploading PDF to server:', filename);
      
      const response = await api.post('/upload-pdf', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      logger.log('✅ PDF uploaded successfully:', response.data);
      return response.data;
    } catch (error: any) {
      logger.error('❌ Failed to upload PDF:', error);
      throw error;
    }
  }

  // Send PDF via email
  async sendPDFEmail(email: string, pdfUrl: string, patientName?: string): Promise<any> {
    try {
      logger.log('📧 Sending PDF email to:', email);
      
      const response = await api.post('/send-pdf-email', {
        email,
        pdf_url: pdfUrl,
        patient_name: patientName || 'Patient'
      });
      
      logger.log('✅ Email sent successfully');
      return response.data;
    } catch (error: any) {
      logger.error('❌ Failed to send email:', error);
      throw error;
    }
  }

  // Get Scan Records
  async getScanRecords(limit: number = 100): Promise<any[]> {
    try {
      const response = await api.get('/scan-records', {
        params: { limit }
      });
      logger.log('✅ Retrieved scan records');
      return response.data.records || [];
    } catch (error: any) {
      logger.error('❌ Failed to get scan records:', error);
      // Fall back to localStorage
      return JSON.parse(localStorage.getItem('orthoMindScans') || '[]');
    }
  }

  // Get PDF URLs for a scan record
  async getPDFUrls(scanRecordId: string): Promise<string[]> {
    try {
      const response = await api.get(`/scan-records/${scanRecordId}/pdfs`);
      logger.log('✅ Retrieved PDF URLs');
      return response.data.pdf_urls || [];
    } catch (error: any) {
      logger.error('❌ Failed to get PDF URLs:', error);
      return [];
    }
  }

  // Create Smile User with analysis criteria
  async createSmileUser(userData: {
    fullName: string;
    email: string;
    notes?: string;
    criteria?: any;
  }): Promise<any> {
    try {
      logger.log('👤 Creating smile user:', userData);
      console.log('🔄 createSmileUser called with:', userData);
      
      const payload = {
        full_name: userData.fullName,
        email: userData.email,
        notes: userData.notes || `Smart Scan analysis performed on ${new Date().toLocaleDateString()}`,
        criteria: userData.criteria || {}
      };
      
      console.log('📤 Sending POST to /smile-users with payload:', payload);
      const response = await api.post('/smile-users', payload);
      console.log('📥 Response from /smile-users:', response.data);
      logger.log('✅ Smile user created successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Error in createSmileUser:', error.response || error);
      logger.error('❌ Failed to create smile user:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService();
